import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Ship, Globe2, Package, Truck, ClipboardCheck, Wheat, Handshake } from "lucide-react";
import SectionHeader from "@/components/common/SectionHeader";
import ProductCard from "@/components/common/ProductCard";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/data/constants";

const HERO = "https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80";
const TRUST = [
  { icon: Globe2, label: "India-Based Sourcing" },
  { icon: Package, label: "B2B Bulk Supply" },
  { icon: ClipboardCheck, label: "Buyer-Specific Specifications" },
  { icon: Ship, label: "Domestic & International Trade" },
];

const CATEGORY_IMAGES = {
  "Rice & Paddy Products": "https://customer-assets-eiarnc6j.emergentagent.net/job_aaha-traders/artifacts/b763vslk_RIce%20Home%20image.png",
  "Pulses & Lentils": "https://customer-assets-eiarnc6j.emergentagent.net/job_aaha-traders/artifacts/77asqnvh_Pulses%26Lentils.jpg",
  "Maize & Cereals": "https://images.unsplash.com/photo-1568584477802-91bcf4a469da?w=800&q=80&auto=format",
  "Millets": "https://customer-assets-eiarnc6j.emergentagent.net/job_aaha-traders/artifacts/ep00olrm_Millets.jpg",
  "Spices": "https://customer-assets-eiarnc6j.emergentagent.net/job_aaha-traders/artifacts/39fsod8w_Spices.jpg",
  "Oilseeds": "https://customer-assets-eiarnc6j.emergentagent.net/job_aaha-traders/artifacts/p7loda5e_Oilseeds.jpg",
  "Other Agricultural Commodities": "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&q=80&auto=format",
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    document.title = "Aaha Kanakam Agri Traders — Quality Agricultural Commodities. Reliable Global Supply.";
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => setFeatured(data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative bg-forest text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-24 pb-28 lg:pt-32 lg:pb-40 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow text-gold mb-6">Merchant Exporter · India</div>
            <h1 data-testid="hero-title" className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
              Quality Agricultural<br className="hidden sm:block" /> Commodities.<br />
              <span className="text-gold italic">Reliable</span> Global Supply.
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
              Sourcing and supplying selected agricultural commodities from India for buyers, distributors, processors and trading partners across domestic and international markets.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/commodities" data-testid="hero-cta-explore" className="btn-gold px-7 py-3.5 text-sm font-semibold tracking-wide inline-flex items-center gap-2">
                Explore Commodities <ArrowRight size={16} />
              </Link>
              <Link to="/quotation" data-testid="hero-cta-quote" className="btn-outline-light px-7 py-3.5 text-sm font-semibold tracking-wide">
                Request a Quotation
              </Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-4">
            <div className="border-l border-gold/30 pl-8 space-y-6">
              <div>
                <div className="font-serif text-5xl text-gold">7+</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Commodity Categories</div>
              </div>
              <div>
                <div className="font-serif text-5xl text-gold">B2B</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Sourcing & Export Coordination</div>
              </div>
              <div>
                <div className="font-serif text-5xl text-gold">MOQ</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">From 1 FCL Container</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-ivory border-y border-borderSubtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-borderSubtle">
          {TRUST.map((t) => (
            <div key={t.label} data-testid={`trust-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 px-4 py-2">
              <t.icon size={22} className="text-gold-600 shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-charcoal tracking-wide uppercase">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeader
            eyebrow="Commodity Categories"
            title="A growing catalogue built for global buyers"
            description="Rice is our foundation. Our sourcing capability extends across pulses, cereals, millets, spices and oilseeds — with new categories added as demand grows."
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, idx) => (
              <Link
                key={c}
                to={`/commodities?category=${encodeURIComponent(c)}`}
                data-testid={`category-tile-${c.toLowerCase().split(" ")[0]}`}
                className="group relative aspect-[5/4] overflow-hidden border border-borderSubtle bg-white card-hover"
              >
                <img src={CATEGORY_IMAGES[c]} alt={c} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2">0{idx + 1}</div>
                  <div className="font-serif text-2xl leading-tight">{c}</div>
                  <div className="mt-3 text-xs flex items-center gap-2 opacity-80 group-hover:opacity-100 group-hover:text-gold transition">
                    View category <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featured.length > 0 && (
        <section className="py-20 md:py-24 bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <SectionHeader eyebrow="Featured Commodities" title="Selected products from our current sourcing catalogue" />
              <Link to="/commodities" className="text-sm text-forest font-semibold link-underline">View all commodities →</Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Why us */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Why Aaha Kanakam"
              title="A dependable sourcing partner for serious buyers"
              description="We coordinate closely with supply partners to match buyer specifications, packaging preferences and delivery requirements — with transparent communication at every step."
            />
            <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-forest link-underline">Learn more about our approach <ArrowRight size={14} /></Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Handshake, title: "Transparent Communication", body: "Clear pricing structure, honest availability status, and specification-first conversations." },
              { icon: ShieldCheck, title: "Quality Awareness", body: "Coordinated pre-shipment checks and documentation aligned with buyer requirements." },
              { icon: Wheat, title: "Reliable Sourcing", body: "A network of supply partners across India's major agricultural belts." },
              { icon: Truck, title: "Trade Execution", body: "Packaging, documentation, freight and logistics coordination end-to-end." },
            ].map((f) => (
              <div key={f.title} className="p-6 bg-white border border-borderSubtle card-hover">
                <div className="w-11 h-11 bg-forest text-gold flex items-center justify-center mb-4"><f.icon size={20} /></div>
                <div className="font-serif text-xl text-forest mb-2">{f.title}</div>
                <p className="text-sm text-slateneutral leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 bg-forest text-white overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-8">
          <div className="eyebrow text-gold mb-6">Ready to source?</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Looking for a reliable agricultural<br className="hidden md:block" /> commodity sourcing partner?
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/70 leading-relaxed">
            Share your product requirements, quantity and destination. Our team will review the request and explore suitable sourcing options.
          </p>
          <Link to="/quotation" data-testid="cta-request-quote" className="mt-10 inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm font-semibold tracking-wide">
            Request a Quotation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
